import { useState, useEffect, useMemo } from 'react';
import {
    Container, Typography, Box, Card, CardContent, Button, Paper,
    Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Chip, Alert, CircularProgress, IconButton, Tooltip,
    TableSortLabel, alpha
} from '@mui/material';
import {
    Add, Sync, CheckCircle, Delete, Edit, Description as DescriptionIcon,
    ArrowUpward, ArrowDownward
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AddClientForm from '../forms/AddClientForm';
import EditClientForm from '../forms/EditClientForm';
import ClientDetailsDrawer from './ClientDetailsDrawer';
import VisibilityIcon from '@mui/icons-material/Visibility';

const GestionClient = () => {
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openAddClient, setOpenAddClient] = useState(false);
    const [editingClient, setEditingClient] = useState(null);
    const [syncingClientId, setSyncingClientId] = useState(null);
    const [syncResults, setSyncResults] = useState(null);
    const [selectedClient, setSelectedClient] = useState(null);
    const [openDetails, setOpenDetails] = useState(false);
    const [banques, setBanques] = useState([]);
    
    // État pour le tri
    const [sortConfig, setSortConfig] = useState({
        field: 'codeClient',
        direction: 'asc'
    });
    
    const navigate = useNavigate();

    // Palette unifiée inspirée du LoginComptable
    const palette = useMemo(
        () => ({
            navy: '#0B1E3C',
            navy2: '#102B55',
            teal: '#18B6B0',
            teal2: '#39D0C9',
            tealLight: '#E0F7F6',
            green: '#7BC043',
            gold: '#F3C15C',
            paper: '#FFFFFF',
            gradient: 'linear-gradient(135deg, #F8FAFC 0%, #EFF6FF 100%)',
            shadow: '0 10px 40px rgba(11, 30, 60, 0.08)',
            textPrimary: '#1E293B',
            textSecondary: '#64748B'
        }),
        []
    );

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [dashboardResponse, clientsResponse, banquesResponse] = await Promise.all([
                axios.get('/api/comptable/dashboard'),
                axios.get('/api/comptable/clients'),
                axios.get('/api/banques')
            ]);
            setClients(clientsResponse.data);
            setBanques(banquesResponse.data);
        } catch (error) {
            console.error('Erreur chargement données:', error);
        } finally {
            setLoading(false);
        }
    };

    // Fonction de tri
    const sortedClients = useMemo(() => {
        const sortableClients = [...clients];
        
        sortableClients.sort((a, b) => {
            let aValue = a[sortConfig.field];
            let bValue = b[sortConfig.field];
            
            // Traitement spécial pour le code client (tri numérique)
            if (sortConfig.field === 'codeClient') {
                aValue = parseInt(aValue) || 0;
                bValue = parseInt(bValue) || 0;
            }
            
            if (aValue < bValue) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
        
        return sortableClients;
    }, [clients, sortConfig]);

    const handleSort = (field) => {
        let direction = 'asc';
        
        if (sortConfig.field === field && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        
        setSortConfig({ field, direction });
    };

    const handleViewDetails = async (clientId) => {
        try {
            const response = await axios.get(`/api/comptable/clients/${clientId}`);
            setSelectedClient(response.data);
            setOpenDetails(true);
        } catch (err) {
            alert('Erreur lors de la récupération des détails du client.');
        }
    };

    const handleSyncClient = async (clientId) => {
        setSyncingClientId(clientId);
        try {
            const response = await axios.post(`/api/tej/sync/${clientId}`);
            setSyncResults(response.data);
            await loadData();
        } catch (error) {
            setSyncResults({ success: false, error: error.response?.data?.message || 'Erreur de synchronisation' });
        } finally {
            setSyncingClientId(null);
        }
    };

    const handleDeleteClient = async (clientId) => {
        if (!window.confirm('Voulez-vous vraiment supprimer ce client ?')) return;

        try {
            await axios.delete(`/api/comptable/clients/${clientId}`);
            await loadData();
        } catch (error) {
            alert(error.response?.data?.error || 'Erreur lors de la suppression.');
        }
    };

    const handleEditClient = (client) => {
        setEditingClient(client);
    };

    const handleViewPlanComptable = (clientId) => {
        navigate(`/dashboard/clients/${clientId}/plan`);
    };

    if (loading) {
        return (
            <Box 
                display="flex" 
                justifyContent="center" 
                alignItems="center" 
                height="80vh"
                sx={{
                    background: palette.gradient,
                    color: palette.textPrimary
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                minHeight: '100vh',
                background: palette.gradient,
                color: palette.textPrimary,
                py: 4,
                px: 2
            }}
        >
            <Container maxWidth="xl">
                {syncResults && (
                    <Alert 
                        severity={syncResults.success ? 'success' : 'error'} 
                        sx={{ 
                            mb: 3, 
                            borderRadius: 2,
                            boxShadow: palette.shadow,
                            border: `1px solid ${syncResults.success ? alpha('#4caf50', 0.2) : alpha('#f44336', 0.2)}`
                        }}
                        onClose={() => setSyncResults(null)}
                    >
                        {syncResults.success
                            ? `Synchronisation réussie. ${syncResults.totalNewDocuments || 0} nouveaux documents.`
                            : `Erreur: ${syncResults.error}`}
                    </Alert>
                )}

                <Card 
                    elevation={0}
                    sx={{ 
                        borderRadius: 4,
                        overflow: 'hidden',
                        bgcolor: 'white',
                        boxShadow: palette.shadow,
                        border: `1px solid ${alpha(palette.teal, 0.1)}`
                    }}
                >
                    {/* En-tête coloré avec dégradé */}
                    <Box
                        sx={{
                            py: 3,
                            px: 4,
                            background: `linear-gradient(135deg, ${palette.navy} 0%, rgba(20,45,80,0.85) 100%)`,
                            color: 'white',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}
                    >
                        <Box>
                            <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                Mes Clients TEJ
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                Gérez et synchronisez vos clients
                            </Typography>
                        </Box>
                        <Button 
                            variant="contained" 
                            startIcon={<Add />} 
                            onClick={() => setOpenAddClient(true)}
                            sx={{
                                py: 1.2,
                                px: 3,
                                borderRadius: 2,
                                fontWeight: 700,
                                fontSize: '0.9rem',
                                color: 'white',
                                background: `linear-gradient(135deg, ${palette.teal} 0%, ${palette.teal2} 100%)`,
                                textTransform: 'none',
                                transition: 'all 0.3s',
                                boxShadow: '0 4px 20px rgba(24, 182, 176, 0.3)',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 8px 30px rgba(24, 182, 176, 0.4)',
                                    background: `linear-gradient(135deg, ${palette.teal2} 0%, ${palette.green} 100%)`
                                },
                                '&:active': {
                                    transform: 'translateY(0)'
                                }
                            }}
                        >
                            Ajouter Client
                        </Button>
                    </Box>

                    <Box sx={{ p: 4 }}>
                        {sortedClients.length > 0 ? (
                            <TableContainer 
                                component={Paper} 
                                elevation={0}
                                sx={{ 
                                    borderRadius: 3,
                                    border: `1px solid ${alpha(palette.teal, 0.1)}`,
                                    overflow: 'auto',
                                    background: 'transparent',
                                    maxWidth: '100%'
                                }}
                            >
                                <Table sx={{ minWidth: 900 }}>
                                    <TableHead>
                                        <TableRow sx={{ 
                                            backgroundColor: alpha(palette.navy, 0.05),
                                            borderBottom: `2px solid ${alpha(palette.teal, 0.2)}`
                                        }}>
                                            <TableCell sx={{ py: 2.5, width: '15%' }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: palette.textPrimary }}>
                                                    Nom
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ py: 2.5, width: '12%' }}>
                                                <TableSortLabel
                                                    active={sortConfig.field === 'codeClient'}
                                                    direction={sortConfig.direction}
                                                    onClick={() => handleSort('codeClient')}
                                                    sx={{ fontWeight: 700, color: palette.textPrimary }}
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        Code Client
                                                        {sortConfig.field === 'codeClient' && (
                                                            sortConfig.direction === 'asc' ? 
                                                            <ArrowUpward fontSize="small" /> : 
                                                            <ArrowDownward fontSize="small" />
                                                        )}
                                                    </Box>
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell sx={{ py: 2.5, width: '10%' }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: palette.textPrimary }}>
                                                    Type
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ py: 2.5, width: '20%' }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: palette.textPrimary }}>
                                                    Email
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ py: 2.5, width: '15%' }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: palette.textPrimary }}>
                                                    Code TVA
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ py: 2.5, width: '10%' }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: palette.textPrimary }}>
                                                    Détails
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ py: 2.5, width: '18%' }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: palette.textPrimary }}>
                                                    Actions
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {sortedClients.map((client, index) => (
                                            <TableRow 
                                                key={client._id}
                                               
                                            >
                                                <TableCell sx={{ py: 2.5 }}>
                                                    <Typography sx={{ fontWeight: 600, color: palette.textPrimary }}>
                                                        {client.raisonSociale || `${client.prenom} ${client.nom}`}
                                                     
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ py: 2.5 }}>
                                                    <Chip 
                                                        label={client.codeClient} 
                                                        size="small"
                                                        sx={{ 
                                                            backgroundColor: alpha(palette.teal, 0.1),
                                                            color: palette.teal,
                                                            fontWeight: 700,
                                                            borderRadius: 2,
                                                            border: `1px solid ${alpha(palette.teal, 0.2)}`
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ py: 2.5 }}>
                                                    <Chip 
                                                        label={client.typePersonne} 
                                                        size="small"
                                                        sx={{ 
                                                            backgroundColor: alpha(palette.green, 0.1),
                                                            color: palette.green,
                                                            fontWeight: 600,
                                                            borderRadius: 2,
                                                            border: `1px solid ${alpha(palette.green, 0.2)}`
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ py: 2.5 }}>
                                                    <Typography 
                                                        variant="body2" 
                                                        sx={{ 
                                                            color: palette.textSecondary,
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                            maxWidth: '250px'
                                                        }}
                                                    >
                                                        {client.email}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ py: 2.5 }}>
                                                    <Typography variant="body2" sx={{ color: palette.textSecondary }}>
                                                        {client.codeTva || '-'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ py: 2.5 }}>
                                                    <Tooltip title="Voir les détails">
                                                        <IconButton
                                                            onClick={() => handleViewDetails(client._id)}
                                                            size="small"
                                                            sx={{ 
                                                                backgroundColor: alpha(palette.teal, 0.1),
                                                                color: palette.teal,
                                                                '&:hover': {
                                                                    backgroundColor: alpha(palette.teal, 0.2),
                                                                    transform: 'translateY(-2px)',
                                                                    boxShadow: `0 4px 12px ${alpha(palette.teal, 0.3)}`
                                                                },
                                                                transition: 'all 0.3s'
                                                            }}
                                                        >
                                                            <VisibilityIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                                <TableCell sx={{ py: 2.5 }}>
                                                    <Box display="flex" gap={1} flexWrap="wrap">
                                                        <Tooltip title="Synchroniser">
                                                            <IconButton
                                                                onClick={() => handleSyncClient(client._id)}
                                                                disabled={syncingClientId === client._id}
                                                                size="small"
                                                                sx={{ 
                                                                    backgroundColor: alpha(palette.teal, 0.1),
                                                                    color: palette.teal,
                                                                    '&:hover': {
                                                                        backgroundColor: alpha(palette.teal, 0.2),
                                                                        transform: 'translateY(-2px)',
                                                                        boxShadow: `0 4px 12px ${alpha(palette.teal, 0.3)}`
                                                                    },
                                                                    transition: 'all 0.3s'
                                                                }}
                                                            >
                                                                {syncingClientId === client._id ? (
                                                                    <CircularProgress size={20} />
                                                                ) : (
                                                                    <Sync fontSize="small" />
                                                                )}
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Voir Plan Comptable">
                                                            <IconButton
                                                                onClick={() => handleViewPlanComptable(client._id)}
                                                                size="small"
                                                                sx={{ 
                                                                    backgroundColor: alpha(palette.navy, 0.1),
                                                                    color: palette.navy,
                                                                    '&:hover': {
                                                                        backgroundColor: alpha(palette.navy, 0.2),
                                                                        transform: 'translateY(-2px)',
                                                                        boxShadow: `0 4px 12px ${alpha(palette.navy, 0.3)}`
                                                                    },
                                                                    transition: 'all 0.3s'
                                                                }}
                                                            >
                                                                <DescriptionIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Modifier">
                                                            <IconButton
                                                                onClick={() => handleEditClient(client)}
                                                                size="small"
                                                                sx={{ 
                                                                    backgroundColor: alpha(palette.gold, 0.1),
                                                                    color: palette.gold,
                                                                    '&:hover': {
                                                                        backgroundColor: alpha(palette.gold, 0.2),
                                                                        transform: 'translateY(-2px)',
                                                                        boxShadow: `0 4px 12px ${alpha(palette.gold, 0.3)}`
                                                                    },
                                                                    transition: 'all 0.3s'
                                                                }}
                                                            >
                                                                <Edit fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Supprimer">
                                                            <IconButton
                                                                onClick={() => handleDeleteClient(client._id)}
                                                                size="small"
                                                                sx={{ 
                                                                    backgroundColor: alpha('#f44336', 0.1),
                                                                    color: '#f44336',
                                                                    '&:hover': {
                                                                        backgroundColor: alpha('#f44336', 0.2),
                                                                        transform: 'translateY(-2px)',
                                                                        boxShadow: `0 4px 12px ${alpha('#f44336', 0.3)}`
                                                                    },
                                                                    transition: 'all 0.3s'
                                                                }}
                                                            >
                                                                <Delete fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : (
                            <Alert 
                                severity="info" 
                                sx={{ 
                                    borderRadius: 3,
                                    border: `1px solid ${alpha(palette.teal, 0.2)}`,
                                    backgroundColor: alpha(palette.tealLight, 0.3),
                                    color: palette.textPrimary,
                                    boxShadow: palette.shadow
                                }}
                            >
                                Aucun client trouvé. Cliquez sur "Ajouter Client" pour commencer.
                            </Alert>
                        )}
                    </Box>
                </Card>

                <AddClientForm
                    open={openAddClient}
                    onClose={() => setOpenAddClient(false)}
                    onSuccess={(newClientId) => {
                        handleSyncClient(newClientId);
                        loadData();
                        setOpenAddClient(false);
                    }}
                    banques={banques}
                />
                <EditClientForm
                    open={!!editingClient}
                    onClose={() => setEditingClient(null)}
                    onSuccess={loadData}
                    initialValues={editingClient}
                    banques={banques}
                />
                <ClientDetailsDrawer
                    open={openDetails}
                    onClose={() => setOpenDetails(false)}
                    client={selectedClient}
                    banques={banques}
                />
            </Container>
        </Box>
    );
};

export default GestionClient;